import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Link, LangRedirect } from "@/components/LangLink";
import { Check, ChevronLeft, ArrowRight, CalendarCheck } from "lucide-react";
import { CTASection, SectionHeading } from "@/components/sections";
import { ProductGallery, ColorSelector, RequestInfoModal } from "@/components/product-detail";
import { getLumavenLine, LUMAVEN_HEALTH_NOTE } from "@/lib/lumaven";
import { useAssistant } from "@/contexts/AssistantContext";

const EXE_IMG = {
  gambaletto: "/products/lumaven-preventive/gambaletto.jpg",
  autoreggente: "/products/lumaven-preventive/autoreggente.jpg",
  monocollant: "/products/lumaven-preventive/collant.jpg",
  "collant-gestante": "/products/lumaven-preventive/collant.jpg",
  "collant-calibrato": "/products/lumaven-preventive/collant.jpg",
};

const LumavenLine = () => {
  const { slug } = useParams();
  const line = getLumavenLine(slug);

  const [exe, setExe] = useState(line?.executions?.[0]?.id);
  const [color, setColor] = useState(line?.colors?.[0]?.id);
  const [modalOpen, setModalOpen] = useState(false);

  const { setProductCtx } = useAssistant();
  useEffect(() => {
    if (!line) return;
    const comp = line.composition.map((m) => `${m[0]} ${m[1]}`).join(", ");
    const exes = line.executions.map((e) => e.label).join(", ");
    setProductCtx({
      title: `LUMAVEN ${line.name}`,
      url: `/it/prodotti/lumaven/${line.slug}`,
      text: `SCHEDA PRODOTTO: LUMAVEN ${line.name} (calza preventiva, marchio MEDISISTEM)\nDescrizione: ${line.description}\nEsecuzioni disponibili: ${exes}\nColori disponibili: ${line.colors.map((c) => c.name).join(", ")}\nComposizione: ${comp}\nNota: "${line.name}" è il nome della linea, non una classe di compressione né un valore sanitario.`,
    });
    return () => setProductCtx(null);
  }, [line, setProductCtx]);

  if (!line) return <LangRedirect to="/prodotti/lumaven" />;

  const currentExe = line.executions.find((e) => e.id === exe) || line.executions[0];
  const summary = {
    Prodotto: `LUMAVEN ${line.name}`,
    Esecuzione: currentExe?.label,
    Colore: line.colors.find((c) => c.id === color)?.name,
  };

  return (
    <>
      <section className="pt-[92px] lg:pt-[104px] pb-10 lg:pb-14 bg-white" data-testid={`lumaven-line-${line.slug}`}>
        <div className="container-x">
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground" aria-label="breadcrumb">
            <Link to="/prodotti/lumaven" className="hover:text-green">LUMAVEN</Link>
            <span>/</span>
            <span className="text-navy">{line.name}</span>
          </nav>

          <div className="mt-6 grid gap-10 lg:grid-cols-2">
            {/* Galleria (placeholder neutri finché non arrivano le foto ufficiali) */}
            <div>
              <ProductGallery key={exe} images={EXE_IMG[exe] ? [EXE_IMG[exe]] : []} label="Foto in arrivo" testid="lumaven-gallery" />
              <p className="mt-3 text-xs italic text-muted-foreground">Foto dell&apos;esecuzione (colore Nero). Disponibile anche in Chiaro.</p>
            </div>

            {/* Dettagli */}
            <div>
              <SectionHeading
                as="h1"
                size="lg"
                maxW="max-w-none"
                eyebrow="Linea preventiva"
                title={line.displayTitle}
                intro={line.description}
              />

              {/* Selettore esecuzioni */}
              <div className="mt-8" data-testid="lumaven-executions">
                <div className="text-sm font-semibold text-navy">Esecuzione</div>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {line.executions.map((e) => {
                    const on = e.id === exe;
                    return (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => setExe(e.id)}
                        aria-pressed={on}
                        data-testid={`lumaven-exe-${e.id}`}
                        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                          on ? "border-green bg-green text-white" : "border-[#E7EDF2] text-navy/70 hover:border-green hover:text-green"
                        }`}
                      >
                        {on && <Check size={15} />}
                        {e.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-sm text-navy" data-testid="lumaven-exe-selected">
                  Esecuzione selezionata: <strong className="font-semibold">{currentExe?.label}</strong>
                </p>
              </div>

              {/* Colori */}
              <div className="mt-8">
                <div className="text-sm font-semibold text-navy">Colore</div>
                <div className="mt-3">
                  <ColorSelector colors={line.colors} value={color} onChange={setColor} />
                </div>
                <p className="mt-2 text-xs italic text-muted-foreground">«Chiaro» è la denominazione provvisoria del colore.</p>
              </div>

              {/* Composizione */}
              <div className="mt-8" data-testid="lumaven-composition">
                <div className="text-sm font-semibold text-navy">Composizione</div>
                <ul className="mt-3 space-y-1.5">
                  {line.composition.map((m) => (
                    <li key={m[0]} className="flex items-center justify-between border-b border-[#E7EDF2] py-1.5 text-sm">
                      <span className="text-muted-foreground">{m[0]}</span>
                      <span className="font-semibold text-navy">{m[1]}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Azioni */}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button onClick={() => setModalOpen(true)} data-testid="lumaven-request-info" className="btn-green inline-flex justify-center">
                  Richiedi informazioni <ArrowRight size={16} />
                </button>
                <Link to="/il-metodo#prenota" data-testid="lumaven-book" className="btn-outline inline-flex justify-center">
                  Prenota una misurazione <CalendarCheck size={16} />
                </Link>
              </div>

              <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{LUMAVEN_HEALTH_NOTE}</p>

              <Link to="/prodotti/lumaven" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-green hover:gap-2.5 transition-all">
                <ChevronLeft size={16} /> Torna a LUMAVEN
              </Link>
            </div>
          </div>
        </div>
      </section>

      <RequestInfoModal open={modalOpen} onOpenChange={setModalOpen} summary={summary} />
      <CTASection />
    </>
  );
};

export default LumavenLine;
